import {ChangeDetectorRef, Component, Inject, Input, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {InSubject} from '@atmi/core/utils';

import {BehaviorSubject, combineLatest, firstValueFrom} from 'rxjs';
import {filter, first, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';

import {BaseCtrlValueAccessorFormComponent} from '@sim/form';
import {ClassName} from '@sim/role';

import {BaseApiService, CardsApiService, filePath, SwalService} from '@atmi/core';
import {Base, cardOA, EBaseType} from '@atmi/core/models';
import {CardOA, IBaseShort} from '@atmi/core/models/cards/card-oa';
import {BasePickerDialogComponent} from '@atmi/shared/base-picker-dialog/base-picker-dialog.component';
import {makeQueryBuilder, makeQueryRules} from '@ui/ui.module';
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {MediaService} from '@sim/media/services/media.service';
import {Media, UIMediaService} from '@sim/media';

@Component({
  selector: 'app-info-card-manager',
  styleUrls: ['./info-card-manager.component.scss'],
  templateUrl: './info-card-manager.component.html',
})
export class InfoCardManagerComponent extends BaseCtrlValueAccessorFormComponent<any> implements OnInit {
  readonly EBaseType = EBaseType;

  @Input() classNames: ClassName[] = [];

  @Input() @InSubject() cardId?: string;
  private readonly cardIdChange = new BehaviorSubject<string | undefined>(undefined);

  @Input() set isOperaMadre(v: null | boolean) {
    this.isOperaMadreEnableControl.setValue(v);
  }

  readonly isOperaMadreEnableControl = new UntypedFormControl(false);

  readonly childNumberCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({benePadre: cardId, tipoOperaFiglia: 'Numero'}, cardOA.CardOAFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexOA({page: 1, limit: 1, filter: query});
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly childProvaCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({
        benePadre: cardId,
        tipoOperaFiglia: 'Prova d\'Artista - EA'
      }, cardOA.CardOAFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexOA({page: 1, limit: 1, filter: query});
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly childFuoriCommercioCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({
        benePadre: cardId,
        tipoOperaFiglia: 'Fuori Commercio - HC'
      }, cardOA.CardOAFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexOA({page: 1, limit: 1, filter: query});
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly productExamples$ = combineLatest([
    this.childNumberCount$,
    this.childFuoriCommercioCount$,
    this.childProvaCount$,
  ]).pipe(
    map(([numberCount, fuoriCommercio, prova]) => numberCount + fuoriCommercio + prova),
  );

  base?: Base;
  basi: (Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } })[] = [];
  parentCard?: CardOA;

  constructor(
    private readonly baseApi: BaseApiService,
    private readonly cardsApi: CardsApiService,
    private readonly dialog: MatDialog,
    private readonly cd: ChangeDetectorRef,
    private swal: SwalService,
    private readonly mediaApi: MediaService,
    private readonly uiMediaService: UIMediaService,
    @Inject(filePath) private readonly fileUrl: string,
  ) {
    super();

    this.formChange
      .pipe(
        filter((form): form is UntypedFormGroup => !!form),
        takeUntil(this.unsubscription$),
      )
      .subscribe((form) => {
        const base: string = form.value.base || '';
        const basi: cardOA.IBaseShort[] = form.value.basi || [];
        /*fix basi if not setup server side*/
        if (base && basi.length === 0) {
          const b = {base, tipo: EBaseType.primary};
          basi.push(b);
          const basiForm = form.controls.basi as FormArray
          basiForm.push(new FormControl(b));
        }
        if (base) this.getBase(base);
        this.getParentCard(form.value.benePadre);
        this.getBasi(basi);
      });
  }

  ngOnInit() {
    super.ngOnInit();
  }

  async showAddBaseDialogHandler() {
    const ref = this.dialog.open(BasePickerDialogComponent, {
      data:
        {basi: this.basi},
      width: '1000px'
    });
    const res: Base | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));
    if (res && this.form) {
      let btipo = EBaseType.secondary;
      const primary = Boolean(this.basi.length && this.basi.find((b) => b.tipo === EBaseType.primary));
      if (this.basi.length === 0) btipo = EBaseType.primary;
      if (!primary) btipo = EBaseType.primary;
      //@ts-ignore
      this.form.controls.basi.push(new FormGroup({
        base: new UntypedFormControl(res.id),
        tipo: new UntypedFormControl(btipo)
      }));

      res.tipo = btipo;
      this.basi.push(res);
      if (btipo === EBaseType.primary) {
        this.form.controls.base.setValue(res.id);
        await this.getBase(res.id);
      }
    }
  }

  unlinkParentCardHandler() {
    if (!this.form) {
      throw new Error('Cannot use form without a value');
    }

    this.form.controls.benePadre.setValue(null);
    this.parentCard = undefined;
  }

  handleBaseTipoChange($event: MatSlideToggleChange, base: Base) {

    const baseId = base.id;

    if ($event.checked) {
      const pidx = this.basi.findIndex((b) => b.tipo === EBaseType.primary);
      if (pidx !== -1) {
        this.basi[pidx].tipo = EBaseType.secondary;
        //@ts-ignore
        this.form?.controls.basi.controls[pidx].setValue({
          base: this.basi[pidx].id,
          tipo: EBaseType.secondary
        });
      }
    }

    const idx = this.basi.findIndex((b) => b.id === baseId);
    const tipo = $event.checked ? EBaseType.primary : EBaseType.secondary;
    //@ts-ignore
    this.form?.controls.basi.controls[idx].setValue({
      base: baseId,
      tipo
    });
    this.basi[idx].tipo = tipo;
    if ($event.checked) {
      this.form?.controls.base.setValue(baseId);
      this.base = base;
    }
  }

  async handleRemoveBase($event: MouseEvent, base: Base) {
    $event.preventDefault();
    const baseId = base.id;

    const res = await this.swal.showConfirm({
      title: 'Attenzione!',
      text: 'Stai per rimuovere la base "' + base.titolo + '" vuoi procedere?'
    });

    if (!res) return;

    const idx = this.basi.findIndex((b) => b.id === baseId);
    this.basi = this.basi.filter((b) => b.id !== baseId);
    //@ts-ignore
    this.form.controls.basi.removeAt(idx);

    if (this.basi.filter((b) => b.tipo === EBaseType.primary).length === 0) {
      this.basi[0].tipo = EBaseType.primary;
      //@ts-ignore
      this.form?.controls.basi.controls[idx].setValue({base: baseId, tipo: EBaseType.primary});
      //@ts-ignore
      this.form.controls.base.setValue(this.basi[0].id);
      this.base = this.basi[0];
    }
  }

  removeBaseHandler() {
    /*to be called only if all bases have been removed*/
    if (!this.form) {
      throw new Error('Cannot use form without a value');
    }

    this.form.controls.base.setValue(null);
    this.base = undefined;
  }

  getProductExamplesToProduct(productExamples: number | null) {
    if (this.form) {
      const operaMadre = this.form.controls.operaMadre as UntypedFormGroup;
      const tiratura = operaMadre.controls.tiratura.value;
      const tiraturaEA = operaMadre.controls.tiraturaEA.value;
      const tiraturaHC = operaMadre.controls.tiraturaHC.value;
      return (tiratura + tiraturaEA + tiraturaHC) - (productExamples || 0);
    }
    return productExamples;
  }

  private async setBaseThumb(base: Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } }) {
    const firstImg = (base.allegati || []).find(a => (a as any).tipo === 'immagine') || (base.allegati || [])[0];
    const fileId = firstImg?.file;
    if (!fileId) return;
    try {
      const media = await firstValueFrom(this.mediaApi.get(fileId).pipe(first()));
      if (media) {
        base.thumbMedia = { ...(media as Media), id: media.id as string, previewUrl: media.previewUrl ? `${this.fileUrl}${media.previewUrl}` : media.previewUrl } as Media & { id: string };
        if (media.previewUrl) {
          base.thumbUrl = `${this.fileUrl}${media.previewUrl}`;
        }
      }
    } catch (_) {
      // ignore thumbnail errors
    }
  }

  showBaseImage(base: Base & { thumbMedia?: Media & { id: string } }) {
    if (base.thumbMedia) {
      this.uiMediaService.dispatchViewMediaAction(base.thumbMedia);
    }
  }

  private async getBase(baseId?: string) {
    if (!baseId) {
      this.base = undefined;
      return;
    }

    try {
      const b = await firstValueFrom(this.baseApi.get(baseId).pipe(first()));
      await this.setBaseThumb(b as Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } });
      this.base = b;
      this.cd.markForCheck();
      return this.base;
    } catch (e) {
      throw new Error(e);
    }
  }

  private async getBasi(infoBasi: IBaseShort[] = []): Promise<Base[]> {
    const basiIds = infoBasi.map(b => b.base).filter(Boolean);
    const basi: (Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } })[] = [];
    for (const id of basiIds) {
      try {
        const base = await firstValueFrom(this.baseApi.get(id).pipe(first()));
        const infoBase = infoBasi.find(b => b.base === id);
        base.tipo = infoBase?.tipo;
        await this.setBaseThumb(base as Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } });
        basi.push(base as Base & { thumbUrl?: string, thumbMedia?: Media & { id: string } });
      } catch (e) {
        throw new Error(e);
      }
    }
    this.basi = basi;
    return basi;
  }

  private async getParentCard(cardId?: string) {
    if (!cardId) {
      this.parentCard = undefined;
      return;
    }

    try {
      this.parentCard = await firstValueFrom(this.cardsApi.getOA(cardId).pipe(first()));
      this.cd.markForCheck();
    } catch (e) {
      throw new Error(e);
    }
  }
}
